//create react component sample template
import React, { useEffect, useRef, useState } from 'react';
import { ListBox, Item } from '@adobe/react-spectrum';
import CardComponent from './MessagesCardComponent';
import CardComponentLarge from './MessagesCardComponentLarge';
import ConceptCardComponent from './ConceptCardComponent';
import MessagesCardComponentMobile from './MessagesCardComponentMobile';
import SwipeableMessageRowMobile from './SwipeableMessageRowMobile';
import { BulkSelectionProvider } from '../../../../../components/protected/layout-components/BulkSelectCheckbox';
import {
  deriveUiMessageState,
  isCommentVisible,
  isDeleteVisible,
} from '../../../../../utils/messageResolution';

interface TodoListProps {
  listMessageList: any;
  items: any[];
  isLoading: boolean;
  handleListRowLargeClick: (key: any) => void;
  rowsData: any;
  totalMessageListRec: number;
  selectedKeys: any;
  selectedDataRow: any;

  isMobile: boolean;
  messageType: any;
  listId?: string;
  useMobileCard?: boolean;
  preferLargeCard?: boolean;
  hideSenderEmailOnLargeCard?: boolean;
  onMobileRowOpen?: (key: string) => void;
  onMobileRowDelete?: (key: string) => void;
  onMobileRowReply?: (key: string) => void;
  onMobileRowReplyAll?: (key: string) => void;
  onMobileRowForward?: (key: string) => void;
  onMobileRowComment?: (key: string) => void;
  onMobileRowMove?: (key: string) => void;
  onDesktopRowDragStart?: (item: any | null) => void;
  bulkSelectedKeys?: Set<string>;
  onBulkSelectionChange?: (id: string, nextSelected: boolean) => void;
}

const MessageList: React.FC<TodoListProps> = ({
  listMessageList,
  handleListRowLargeClick,
  rowsData,
  totalMessageListRec,
  selectedKeys,
  isMobile,
  messageType,
  listId = 'listMessageCards',
  useMobileCard = false,
  preferLargeCard = false,
  hideSenderEmailOnLargeCard = false,
  onMobileRowOpen,
  onMobileRowDelete,
  onMobileRowReply,
  onMobileRowReplyAll,
  onMobileRowForward,
  onMobileRowComment,
  onMobileRowMove,
  onDesktopRowDragStart,
  bulkSelectedKeys,
  onBulkSelectionChange,
}) => {
  const [openSwipeRowId, setOpenSwipeRowId] = useState<string | null>(null);
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!useMobileCard) return;
    if (!mobileLoadMoreRef.current) return;
    if (totalMessageListRec <= listMessageList.items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !listMessageList.isLoading) {
          listMessageList.loadMore();
        }
      },
      { rootMargin: "120px" },
    );

    observer.observe(mobileLoadMoreRef.current);

    return () => observer.disconnect();
  }, [
    useMobileCard,
    totalMessageListRec,
    listMessageList,
    listMessageList.items.length,
    listMessageList.isLoading,
  ]);

  const providerEnabled = !!(bulkSelectedKeys && onBulkSelectionChange);
  const providerKeys = bulkSelectedKeys ?? EMPTY_KEYS;
  const providerToggle = onBulkSelectionChange ?? NOOP_TOGGLE;
  const showUnresolvedReferenceIndicator = messageType === "todo";

  if (useMobileCard) {
    const mobileBody = (
      <div
        style={{
          width: "100%",
          minWidth: 0,
          padding: "0 0 0 5px",
          boxSizing: "border-box",
        }}
      >
        {listMessageList.items.map((item: any) => (
          <div key={item.id} style={{ width: "100%", marginBottom: "2px" }}>
            {(() => {
              const uiState = deriveUiMessageState({
                listType: messageType,
                selectedMessage: item,
              });
              const showMove = uiState.viewKind === "original";
              const showDelete = isDeleteVisible(uiState);
              const showComment = isCommentVisible(uiState);
              const showReply = uiState.supportsReply;
              const showForward = uiState.supportsForward;

              return (
            <SwipeableMessageRowMobile
              id={String(item.id)}
              isOpen={openSwipeRowId === String(item.id)}
              onOpen={setOpenSwipeRowId}
              onClose={() => setOpenSwipeRowId(null)}
              onPress={() => onMobileRowOpen?.(String(item.id))}
              onReply={showReply ? () => onMobileRowReply?.(String(item.id)) : undefined}
              onReplyAll={showReply ? () => onMobileRowReplyAll?.(String(item.id)) : undefined}
              onForward={showForward ? () => onMobileRowForward?.(String(item.id)) : undefined}
              onComment={showComment ? () => onMobileRowComment?.(String(item.id)) : undefined}
              onMove={showMove ? () => onMobileRowMove?.(String(item.id)) : undefined}
              onDelete={showDelete ? () => onMobileRowDelete?.(String(item.id)) : undefined}
            >
              <div
                style={{
                  position: "relative",
                  display: "block",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  height: "81px",
                  overflow: "hidden",
                }}
              >
                <MessagesCardComponentMobile
                  item={item}
                  showUnresolvedReferenceIndicator={showUnresolvedReferenceIndicator}
                />
              </div>
            </SwipeableMessageRowMobile>
              );
            })()}
          </div>
        ))}
        {totalMessageListRec > listMessageList.items.length && (
          <div
            ref={mobileLoadMoreRef}
            style={{ width: "100%", height: "1px" }}
          />
        )}
      </div>
    );
    return providerEnabled ? (
      <BulkSelectionProvider
        selectedKeys={providerKeys}
        toggle={providerToggle}
      >
        {mobileBody}
      </BulkSelectionProvider>
    ) : (
      mobileBody
    );
  }

  const listBox = (
    <ListBox
      autoFocus
      id={listId}
      selectionMode="single"
      onSelectionChange={handleListRowLargeClick}
      defaultSelectedKeys={selectedKeys}
      selectedKeys={selectedKeys}
      aria-label={listId}
      items={listMessageList.items}
      isLoading={listMessageList.isLoading}
      onLoadMore={
        totalMessageListRec > listMessageList.items.length
          ? listMessageList.loadMore
          : undefined
      }
      width={'100%'}
      UNSAFE_style={{ padding: '0 0 0 5px' }}
    >
      {(item: any) => (
        <Item key={item.id}>
          <div
            draggable={messageType !== "concept" && !!onDesktopRowDragStart}
            onDragStart={(event) => {
              if (!onDesktopRowDragStart || messageType === "concept") return;
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", String(item.id));
              onDesktopRowDragStart(item);
            }}
            onDragEnd={() => onDesktopRowDragStart?.(null)}
          >
            {messageType === 'concept' ? (
              <ConceptCardComponent item={item} />
            ) : isMobile && !preferLargeCard ? (
              <CardComponent
                item={item}
                showUnresolvedReferenceIndicator={showUnresolvedReferenceIndicator}
              />
            ) : (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "82px",
                }}
              >
                <CardComponentLarge
                  item={item}
                  hideSenderEmail={hideSenderEmailOnLargeCard}
                  showUnresolvedReferenceIndicator={showUnresolvedReferenceIndicator}
                />
              </div>
            )}
          </div>
        </Item>
      )}
    </ListBox>
  );

  return providerEnabled ? (
    <BulkSelectionProvider
      selectedKeys={providerKeys}
      toggle={providerToggle}
    >
      {listBox}
    </BulkSelectionProvider>
  ) : (
    listBox
  );
};

const EMPTY_KEYS: Set<string> = new Set();
const NOOP_TOGGLE = (): void => undefined;

export default MessageList;
